import { useState } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ServiceTabConfig, iconMap, availableIcons } from '@/data/serviceTabPresets';

interface ServiceTabsEditorProps {
  tabs: ServiceTabConfig[];
  onChange: (tabs: ServiceTabConfig[]) => void;
  maxTabs?: number;
}

export const ServiceTabsEditor = ({ tabs, onChange, maxTabs = 5 }: ServiceTabsEditorProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleLabelChange = (id: string, label: string) => {
    onChange(tabs.map(tab => tab.id === id ? { ...tab, label } : tab));
  };

  const handleIconChange = (id: string, icon: string) => {
    onChange(tabs.map(tab => tab.id === id ? { ...tab, icon } : tab));
  };

  const handleRemoveTab = (id: string) => {
    if (tabs.length > 1) {
      onChange(tabs.filter(tab => tab.id !== id));
    }
  };

  const handleAddTab = () => {
    if (tabs.length < maxTabs) {
      const newId = `custom_${Date.now()}`;
      onChange([...tabs, { id: newId, label: 'New Category', icon: 'sparkles' }]);
      setEditingId(newId);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {tabs.map((tab, index) => {
          const Icon = iconMap[tab.icon] || iconMap.sparkles;
          const isEditing = editingId === tab.id;

          return (
            <div
              key={tab.id}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border transition-all',
                isEditing ? 'border-primary bg-primary/5' : 'border-border bg-secondary/30'
              )}
            >
              <div className="text-muted-foreground cursor-grab">
                <OrbitIcon name="line.3.horizontal" fallback={GripVertical} className="w-4 h-4" />
              </div>

              <div className="flex-1 flex items-center gap-3">
                <Select
                  value={tab.icon}
                  onValueChange={(value) => handleIconChange(tab.id, value)}
                >
                  <SelectTrigger className="w-16 h-10">
                    <Icon className="w-4 h-4" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableIcons.map((iconName) => {
                      const IconOption = iconMap[iconName];
                      return (
                        <SelectItem key={iconName} value={iconName}>
                          <div className="flex items-center gap-2">
                            <IconOption className="w-4 h-4" />
                            <span className="capitalize">{iconName}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>

                <Input
                  value={tab.label}
                  onChange={(e) => handleLabelChange(tab.id, e.target.value)}
                  onFocus={() => setEditingId(tab.id)}
                  onBlur={() => setEditingId(null)}
                  className="flex-1"
                  placeholder="Tab name"
                />
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveTab(tab.id)}
                disabled={tabs.length <= 1}
                className="text-muted-foreground hover:text-destructive"
              >
                <OrbitIcon name="trash" fallback={Trash2} className="w-4 h-4" />
              </Button>
            </div>
          );
        })}
      </div>

      {tabs.length < maxTabs && (
        <Button
          variant="outline"
          onClick={handleAddTab}
          className="w-full border-dashed"
        >
          <OrbitIcon name="plus" fallback={Plus} className="w-4 h-4 mr-2" />
          Add Category ({tabs.length}/{maxTabs})
        </Button>
      )}

      {/* Preview */}
      <div className="mt-6 pt-6 border-t border-border">
        <p className="text-sm text-muted-foreground mb-3">Preview</p>
        <div className="flex gap-1 p-1 bg-secondary/50 rounded-xl border border-border overflow-x-auto">
          {/* System "All" tab - always first, non-editable */}
          <div
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap bg-primary/10 text-muted-foreground border border-primary/20"
          >
            <span className="w-4 h-4 flex items-center justify-center">📥</span>
            All Inquiries
            <span className="text-[9px] bg-muted px-1.5 rounded opacity-60">System</span>
          </div>
          {tabs.map((tab, index) => {
            const Icon = iconMap[tab.icon] || iconMap.sparkles;
            return (
              <div
                key={tab.id}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap',
                  index === 0
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'text-muted-foreground'
                )}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full bg-primary/40"></span>
          "All Inquiries" is a permanent system tab that cannot be removed
        </p>
      </div>
    </div>
  );
};
